
import { defineComponent, ref, PropType, onUnmounted, Ref, reactive, computed } from 'vue';

import { required, minValue, maxValue } from '@vuelidate/validators';

import useVuelidate from '@vuelidate/core';

import UserProperty from '@/components/user-settings/UserProperty.vue';
/* import UserButtonProperty from '@/components/user-settings/UserButtonProperty.vue'; */
import { IUserSettings } from '@/pages/layout/Layout.vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { locales } from '@/config/locales';
import { mapSettings } from '@/config/mapSettings';

export default defineComponent({
    components: {
        'user-property': UserProperty,
        /* 'user-button-property': UserButtonProperty, */
    },
    props: {
        isMobile: {
            type: Boolean,
            required: true,
        },
        username: {
            type: String,
            required: true,
        },
        email: {
            type: String,
            required: true,
        },
        defaultSettings: {
            type: String,
            required: true,
        },
        buttonText: {
            type: String,
            required: true,
        },
        userPictureUrl: {
            type: String,
            required: true,
        },
        settings: {
            type: Object as PropType<IUserSettings>,
            required: true,
        },
    },
    emits: ['close'],
    setup(props, { emit }) {
        const i18n = useI18n({ useScope: 'global' });
        const userSettings: Ref<IUserSettings> = ref(props.settings);

        const userSettingsInitial = computed(() => store.getters['auth/userSettings']);

        const languageSettingsOpen = ref(false);
        const mapSettingsOpen = ref(false);
        const currentLanguage = ref(i18n.locale.value);
        const localesList = ref(locales);
        const isUserModalVisible = computed(() => store.getters['ui/isUserModalVisible']);
        const serverLocale = computed(() => store.getters['ui/languageCode']);

        const tokenFCM = computed(() => {
            const value = store.getters['auth/userSettingString']('tokenFCM');
            if (value) {
                try {
                    return JSON.parse(value);
                } catch (e) {
                    console.log('error tokenFCM', e);
                }
            }
            return [];
        });

        const currentDefaultMapSettings = ref(props.defaultSettings);

        const store = useStore();
        const state = reactive({
            firstName: '',
            lastName: '',
            contact: {
                email: '',
            },
        });
        const validationRules = {
            deviceGroupingNumber: {
                value: {
                    required,
                    minValue: minValue(2),
                },
            },
            refreshInterval: {
                value: {
                    required,
                    minValue: minValue(5),
                    maxValue: maxValue(300),
                },
            },
            deviceInactiveTime: {
                value: {
                    required,
                    minValue: minValue(60),
                },
            },
            deviceWithoutContactTime: {
                value: {
                    required,
                    minValue: minValue(60),
                },
            },
        };
        const mapSettingsList = ref(mapSettings);

        const v$ = useVuelidate(validationRules, userSettings);

        onUnmounted(async () => {
            await store.dispatch('auth/account');
            await store.dispatch('device/list');
        });

        const changeLanguage = (lang: string) => {
            currentLanguage.value = lang;
            languageSettingsOpen.value = false;
        };

        const changeDefaultMapSettings = (settings: string) => {
            currentDefaultMapSettings.value = settings;
            mapSettingsOpen.value = false;
        };

        const openTermsAndConditionsModal = () => store.commit('ui/openTermsAndConditionsModal');

        const saveChanges = async () => {
            v$.value.$validate();

            userSettings.value.deviceGroupingNumber.error =
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (v$ as any).value.deviceGroupingNumber.$error;
            userSettings.value.refreshInterval.error =
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (v$ as any).value.refreshInterval.$error;
            userSettings.value.deviceInactiveTime.error =
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (v$ as any).value.deviceInactiveTime.$error;
            userSettings.value.deviceWithoutContactTime.error =
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (v$ as any).value.deviceWithoutContactTime.$error;

            if (v$.value.$errors.length === 0) {
                const userData = {
                    UserSettings: {
                        ...userSettingsInitial.value,
                        'usersetting.customer.devicemap.detailpage.map.countdownsec':
                            userSettings.value.refreshInterval.value,
                        'usersetting.customer.devicemap.defaultpage.map.showtraffic': userSettings.value.switch[0].value
                            ? 'True'
                            : 'False',
                        'usersetting.customer.devicemap.defaultpage.map.devicegroupping': userSettings.value.switch[1]
                            .value
                            ? 'True'
                            : 'False',
                        'usersetting.customer.devicemap.defaultpage.hideManualRefresh': userSettings.value.toolbar[1]
                            .value
                            ? 'True'
                            : 'False',
                        'usersetting.customer.devicemap.defaultpage.hideAutoRefresh': userSettings.value.toolbar[2]
                            .value
                            ? 'True'
                            : 'False',
                        'usersetting.customer.devicemap.defaultpage.hideSearch': userSettings.value.toolbar[3].value
                            ? 'True'
                            : 'False',
                        'usersetting.customer.devicemap.defaultpage.hideSelectLayers': userSettings.value.toolbar[4]
                            .value
                            ? 'True'
                            : 'False',
                        'usersetting.customer.devicemap.defaultpage.hideSelectGeofences': userSettings.value.toolbar[5]
                            .value
                            ? 'True'
                            : 'False',
                        /* allowSubscriptions: userSettings.value.allowSubscriptions.value ? 'True' : 'False', */
                        'usersetting.customer.devicemap.defaultpage.map.devicegroupingnumber':
                            userSettings.value.deviceGroupingNumber.value,
                        'usersetting.customer.devicemap.detailpage.map.deviceinactivetime':
                            userSettings.value.deviceInactiveTime.value,
                        'usersetting.customer.devicemap.detailpage.map.devicewithoutcontacttime':
                            userSettings.value.deviceWithoutContactTime.value,
                        'usersetting.customer.devicemap.detailpage.map.defaultmap': currentDefaultMapSettings.value,
                    },
                    LanguageCode: currentLanguage.value,
                };

                await store.dispatch('auth/updateAccount', userData);
                emit('close');
            }
        };

        const handleUnsubscribeAllDevices = async () => {
            userSettings.value.allowSubscriptions.value = false;
            await store.dispatch('auth/unsubscribeAllDevices');
            await store.dispatch('auth/account');
        };

        return {
            mapSettingsOpen,
            userSettings,
            currentLanguage,
            currentDefaultMapSettings,
            changeDefaultMapSettings,
            localesList,
            languageSettingsOpen,
            mapSettingsList,
            changeLanguage,
            saveChanges,
            state,
            v$,
            isUserModalVisible,
            version: process.env.VUE_APP_VERSION,
            serverLocale,
            tokenFCM,
            openTermsAndConditionsModal,
            handleUnsubscribeAllDevices,
        };
    },
});
